import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-black text-white py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-8">Privacy Policy for Slyder</h1>
        <p className="text-gray-400 mb-8">Last Updated: March 01, 2026</p>

        <div className="space-y-6 text-gray-300">
          <p className="text-lg leading-relaxed">
            This Privacy Policy explains how Ethereal Labs Private Limited ("we," "us," or "our") collects, uses, discloses, and protects your information when you use our mobile application, Slyder (the "Service").
          </p>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">1. Age Requirement</h2>
            <p className="leading-relaxed">
              Slyder is intended exclusively for users who are 18 years of age and older. We do not knowingly collect personal information from anyone under 18. If we learn that we have collected personal information from a user under 18, we will take immediate steps to delete that information and terminate the user's account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">2. Information We Collect</h2>
            <p className="mb-4">We collect the following types of information to provide and improve our Service:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account Information:</strong> Information you provide during registration, such as your email address, username, and password.</li>
              <li><strong>Profile Information:</strong> Information you choose to add to your profile, such as a display name, profile picture, or bio.</li>
              <li><strong>Messages and Content (AI Simulator):</strong> This includes all text, prompts, and chat history you exchange with our AI companions.</li>
              <li><strong>Coaching Feature Data (Dating Coach):</strong> This includes the screenshots, photos, and text (like bios) you upload to our "Chat Reply Generator" and "Profile Analyzer" features.</li>
              <li><strong>Voice Call Data:</strong> Voice audio from AI voice calls you initiate with AI companions.</li>
              <li><strong>Purchase Information:</strong> If you subscribe, our third-party payment processor (e.g., Google Play Store) will collect your payment information. We maintain a record of your subscription status.</li>
              <li><strong>Usage Data:</strong> We automatically collect data about your interaction with the Service, such as features used, in-app actions, timestamps, and crash logs.</li>
              <li><strong>Device Information:</strong> We collect information about your mobile device, such as operating system, device model, IP address, and unique device identifiers.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">3. Community Standards and Prohibited Content</h2>
            <p className="leading-relaxed mb-4">
              To ensure safety, you agree not to create, share, or upload content that is illegal, harmful, threatening, harassing, defamatory, or promotes violence.
            </p>

            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mt-4">
              <p className="font-bold text-white mb-3">Zero-Tolerance Policy on Child Sexual Abuse Material (CSAE):</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Slyder maintains a strict, zero-tolerance policy against CSAE.</li>
                <li>Any content found to be CSAE will be removed immediately.</li>
                <li>Users found creating or sharing CSAE will be permanently banned.</li>
                <li>We will report all such content and associated accounts to the National Center for Missing & Exploited Children (NCMEC) and relevant law enforcement agencies.</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">4. Sensitive Personal Information</h2>
            <p className="leading-relaxed">
              In conversations with AI companions, you may voluntarily provide sensitive personal information (e.g., sexual orientation, religious beliefs, or health). By providing this, you expressly consent to our processing of it to provide the AI chat functionality.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">5. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>To Provide the Service:</strong> To operate the AI Simulator and provide coaching feedback.</li>
              <li><strong>To Enforce Policies:</strong> To ensure security and prevent fraud.</li>
              <li><strong>To Process Coaching Data:</strong> Screenshots and profile data uploaded for coaching are used only for analysis and are automatically deleted from our servers within 24 hours.</li>
              <li><strong>To Improve Services:</strong> To analyze usage data (not chat content) to fix bugs and develop new features.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">6. Data Sharing</h2>
            <p className="mb-4">We do not sell your personal data. We share information only with:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>AI Service Providers:</strong> To process prompts and coaching uploads via our third-party AI model partners. These partners are prohibited from using your data to train their own models.</li>
              <li><strong>Cloud Hosting & Analytics:</strong> To securely store data and understand app performance.</li>
              <li><strong>Legal Compliance:</strong> If required by law or to protect against illegal content (as noted in Section 3).</li>
            </ul>
            <p className="mt-4 leading-relaxed">
              <strong>Our Marketing Promise:</strong> We will never share the content of your AI conversations or uploaded screenshots with third-party advertising or marketing partners.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">7. Data Security</h2>
            <p className="leading-relaxed">
              We implement SSL encryption for data in transit and strict access controls for data stored on our servers. However, no electronic storage is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">8. Your Rights and Data Deletion</h2>
            <p className="mb-2">You may request the deletion of your account and data at any time via:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The 'Delete Account' button in the app Settings.</li>
              <li>Emailing <a href="mailto:admin@slyderapp.com" className="text-purple-400 hover:text-purple-300 transition-colors">admin@slyderapp.com</a> with the subject 'DATA DELETION REQUEST'.</li>
              <li>Visiting <a href="https://slyderapp.com/delete-account" className="text-purple-400 hover:text-purple-300 transition-colors">https://slyderapp.com/delete-account</a>.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">9. Third-Party AI Integrations and Data Use</h2>
            <p className="mb-4 leading-relaxed">
              To provide our AI Dating Simulator and Coaching features, Slyder utilizes third-party Artificial Intelligence services.
            </p>
            <ul className="list-disc pl-6 space-y-3">
              <li><strong>Service Provider:</strong> We share necessary data with Google (Gemini API) to power AI features.</li>
              <li>
                <strong>Data Sent:</strong> To generate AI responses, the following data may be sent to Google:
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Chat messages and text prompts</li>
                  <li>Images and profile screenshots uploaded for coaching</li>
                  <li>Voice audio from AI voice calls</li>
                </ul>
                <p className="mt-2">We do not share your email address, legal name, phone number, or payment information with AI providers.</p>
              </li>
              <li><strong>Purpose:</strong> This data is sent solely to generate AI responses, reply suggestions, profile analysis, and voice interactions. It is not used for advertising, profiling, or any other purpose.</li>
              <li><strong>Data Retention:</strong> Slyder does not store your AI conversation data beyond what is necessary to provide the service.</li>
              <li><strong>Data Protection:</strong> Our third-party AI partner provides the same or equal protection of your personal data as outlined in this policy.</li>
              <li><strong>User Consent:</strong> Before any data is shared with Google, users are prompted within the app to review and explicitly consent to this data sharing.</li>
            </ul>
            <p className="mt-4 leading-relaxed">
              For more information about how Google handles data, please refer to <a href="https://policies.google.com/privacy" className="text-purple-400 hover:text-purple-300 transition-colors" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">10. Contact Us</h2>
            <p className="mb-4">If you have any questions about this Privacy Policy, please contact us at:</p>
            <div className="space-y-2">
              <p><strong>Entity Name:</strong> Ethereal Labs Private Limited</p>
              <p><strong>Address:</strong> F.No.205, 8-2-644/1/205, D'Desks, Hiline Complex, Road No.12, Banjara Hills, Hyderabad, Telangana, 500034.</p>
              <p><strong>Email:</strong> <a href="mailto:admin@slyderapp.com" className="text-purple-400 hover:text-purple-300 transition-colors">admin@slyderapp.com</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
